import Text from 'components/Text/Text';
import React, { useContext, useEffect } from 'react';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import { Form } from 'antd';
import HardwareIdField from './components/HardwareIdField';
import { CustomerField } from './components/CustomerField';
import { RestaurantField } from './components/RestaurantField';
import BankSettingsBlock from './components/BankSettingsBlock/BankSettingsBlock';
import UseTLSCertificate from './components/UseTLSCertificate';
import { TlsCertificateNameFromRestaurant } from './components/TlsCertificateNameFromRestaurant';
import { TlsCertificateNameField } from './components/TlsCertificateNameField';
import TlsFileSection from './components/TlsFileSection';
import BtnBlock from './components/BtnBlock';
import { IDeviceBankSettingForEdit } from 'types';
import { EditDeviceContext, IEditDeviceContext } from 'modules/Devices/EditDevice/editDeviceState/editDeviceContext';
import { NEW_BANK_SETTINGS_BLOCK_PREFIX } from 'appConstants';
import { preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);

	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeHardwareId,
		setIDCustomer,
		setIDRestaurant,
		setFetchedRestaurants,
		changeTlsCertificateName,
		setTlsCertificateFile,
		deviceId,
		hardwareId,
		IDSelectedCustomer,
		IDSelectedRestaurant,
		bankSettings,
		tlsCertificateLevel,
		tlsCertificateName,
		tlsCertificateFilePath,
		tlsCertificateFileId,
		tlsCertificateFileHash,
	} = useContext<IEditDeviceContext>(EditDeviceContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({ hardwareId, IDSelectedCustomer, IDSelectedRestaurant, tlsCertificateName });
	}, [hardwareId, IDSelectedCustomer, IDSelectedRestaurant, tlsCertificateName]);

	const submit = async () => {
		setMainFormValidationFail(false);
		const finalBankSettings: IDeviceBankSettingForEdit[] = bankSettings.map((el) => {
			const isNewBankSettingsBlock =
				el.id?.slice(0, NEW_BANK_SETTINGS_BLOCK_PREFIX.length) === NEW_BANK_SETTINGS_BLOCK_PREFIX;
			return {
				id: isNewBankSettingsBlock ? null : el.id,
				terminalId: el.terminalId,
				restaurantBankSettingId: el.restaurantBankSettingId,
				m4BankSettingCredentials:
					el.m4BankSettingCredentials?.login && el.m4BankSettingCredentials?.password
						? el.m4BankSettingCredentials
						: null,
				toucanBankSettingCredentials:
					el.toucanBankSettingCredentials?.login && el.toucanBankSettingCredentials?.password
						? el.toucanBankSettingCredentials
						: null,
			};
		});

		setDataForSubmit({
			id: deviceId,
			hardwareId: hardwareId || '',
			restaurantId: IDSelectedRestaurant,
			deviceBankSettings: finalBankSettings,
			tlsCertificateName:
				tlsCertificateFileId === null || (tlsCertificateFileId === '' && tlsCertificateFilePath === null)
					? null
					: tlsCertificateName,
			tlsCertificateFileId,
			tlsCertificateFileHash,
		});
		additionalValidationErrors.length
			? setMainFormValidationFail(true)
			: changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFormValidationFail = () => {
		setMainFormValidationFail(true);
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form
				form={form}
				name="basic"
				onFinish={submit}
				onKeyPress={preventDefaultForEnterKey}
				onFinishFailed={handleFormValidationFail}
				layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="hardwareIdHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<HardwareIdField changeHardwareId={changeHardwareId} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<CustomerField setIDCustomer={setIDCustomer} IDSelectedCustomer={IDSelectedCustomer} />
					<RestaurantField
						setFetchedRestaurants={setFetchedRestaurants}
						setIDRestaurant={setIDRestaurant}
						IDSelectedCustomer={IDSelectedCustomer}
						IDSelectedRestaurant={IDSelectedRestaurant}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="bankSettingsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<BankSettingsBlock bankSettings={bankSettings} />
				</div>
				{IDSelectedRestaurant && !['None', null].includes(tlsCertificateLevel) && (
					<div className={style.fieldsWrapper}>
						<Text
							translationNameSpace="form"
							translationKey="clientTLSCertificateHeading"
							theme="secondary"
							size="l"
							weight="bold"
							display="inline"
						/>
						<UseTLSCertificate useTlsCertificate={tlsCertificateLevel === 'Device'} />
						{tlsCertificateLevel === 'Restaurant' && (
							<TlsCertificateNameFromRestaurant tlsCertificateName={tlsCertificateName} />
						)}
						{tlsCertificateLevel === 'Device' && (
							<>
								<TlsCertificateNameField
									changeTlsCertificateName={changeTlsCertificateName}
									tlsCertificateFilePath={tlsCertificateFilePath}
									tlsCertificateFileId={tlsCertificateFileId}
								/>
								<TlsFileSection
									setTlsCertificateFile={setTlsCertificateFile}
									changeTypeOfModalWindow={changeTypeOfModalWindow}
									tlsCertificateFileId={tlsCertificateFileId}
									tlsCertificateFilePath={tlsCertificateFilePath}
								/>
							</>
						)}
					</div>
				)}
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
